import MetaData from '../../components/MetaData'
import ResumeIllustration from '../../assets/Resumes.svg'
import { HiOutlineSparkles, HiOutlineChevronRight, HiOutlineCalendarDays } from "react-icons/hi2";
import { MdCheck, MdVerifiedUser, MdThumbUp, MdTrendingUp, MdAutoAwesome } from "react-icons/md";

function PrepareResume() {
    const features = [
        {
            icon: MdVerifiedUser,
            title: "Professional Experts",
            description: "Crafted by industry experts who know what recruiters look for"
        },
        {
            icon: MdAutoAwesome,
            title: "ATS-Friendly",
            description: "100% optimized to pass Applicant Tracking Systems and get through filters"
        },
        {
            icon: MdTrendingUp,
            title: "Results Proven",
            description: "Our clients get 5x more interview calls with professionally prepared resumes"
        },
        {
            icon: MdThumbUp,
            title: "Personalized Touch",
            description: "Each resume is tailored to your industry, role, and career goals"
        }
    ]

    const benefits = [
        "Stand out from thousands of applicants",
        "Get past automated resume scanners",
        "Impress recruiters in the first 6 seconds",
        "Increase your interview chances by 5x",
        "Save hours of work with expert preparation"
    ]

    const handleBookSlot = () => {
        window.location.href = 'mailto:support@careerconnectai.com?subject=Resume%20Preparation%20-%20Book%20a%20Slot'
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
            <MetaData
                title="AI Resume Creator | Build Professional Resume Online | CareerConnect AI"
                description="Create a professional resume with our AI-powered resume builder. Get ATS-optimized templates, smart suggestions, and land your dream job faster."
                url="https://www.careerconnectai.com/resume-creator"
                keywords="resume creator, resume builder, ATS-optimized resume, professional resume, resume template, job seeker, career tools, resume writer, online resume builder, resume tips, job application, careerconnect ai"
            />

            {/* Hero Section */}
            <section className="min-h-[90vh] flex items-center justify-center px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
                <div className="max-w-7xl w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        {/* Left Content */}
                        <div className="space-y-6 sm:space-y-8">
                            <div className="space-y-3 sm:space-y-4">
                                <div className="inline-block">
                                    <div className="px-4 py-2 bg-blue-100 rounded-full">
                                        <p className="text-sm font-semibold text-[#2C25C4] flex items-center gap-2">
                                            <HiOutlineSparkles className="w-4 h-4" />
                                            Professional Resume Preparation
                                        </p>
                                    </div>
                                </div>

                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                                    We Prepare Your <span className="bg-gradient-to-r from-[#2C25C4] to-[#8C18C3] bg-clip-text text-transparent">ATS-Friendly Resume</span>
                                </h1>

                                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                                    Stop wasting time on DIY resumes. Let our experts prepare a professionally crafted resume that passes ATS systems and gets you noticed by recruiters. Guaranteed to make an impact.
                                </p>
                            </div>

                            {/* CTA Button */}
                            <div className="flex flex-col sm:flex-row gap-4 pt-4">
                                <button
                                    onClick={handleBookSlot}
                                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2C25C4] to-[#8C18C3] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 text-base sm:text-lg"
                                >
                                    <HiOutlineCalendarDays className="w-5 h-5" />
                                    Book a Slot Now
                                </button>
                                <button
                                    onClick={handleBookSlot}
                                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#2C25C4] border-2 border-[#2C25C4] rounded-lg font-semibold hover:bg-blue-50 transition-all duration-300 text-base sm:text-lg"
                                >
                                    Contact Us
                                    <HiOutlineChevronRight className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Key Benefits */}
                            <div className="space-y-3 pt-6">
                                <p className="text-sm text-gray-600 font-semibold">Why professionals choose us:</p>
                                <div className="space-y-2">
                                    {benefits.map((benefit, idx) => (
                                        <p key={idx} className="text-gray-700 flex items-center gap-3">
                                            <MdCheck className="w-5 h-5 text-[#2C25C4] flex-shrink-0 font-bold" />
                                            <span>{benefit}</span>
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Illustration */}
                        <div className="hidden lg:flex items-center justify-center">
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-[#2C25C4]/10 to-[#8C18C3]/10 rounded-full blur-3xl"></div>
                                <img
                                    src={ResumeIllustration}
                                    alt="Professional Resume Preparation"
                                    className="relative w-full h-auto max-w-md drop-shadow-lg"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-8 lg:px-12 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center space-y-4 mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
                            Why Your Resume Matters
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Your resume is your first impression. Make it count with professional preparation
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                        {features.map((feature, idx) => {
                            const Icon = feature.icon
                            return (
                                <div
                                    key={idx}
                                    className="group p-6 sm:p-8 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 hover:border-[#2C25C4] hover:shadow-lg transition-all duration-300"
                                >
                                    <div className="w-12 h-12 bg-gradient-to-br from-[#2C25C4] to-[#8C18C3] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Social Proof Section */}
            <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-8 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center p-8 bg-white rounded-xl border border-slate-200">
                            <p className="text-4xl font-bold text-[#2C25C4] mb-2">5x</p>
                            <p className="text-gray-600">More Interview Calls</p>
                        </div>
                        <div className="text-center p-8 bg-white rounded-xl border border-slate-200">
                            <p className="text-4xl font-bold text-[#2C25C4] mb-2">99%</p>
                            <p className="text-gray-600">ATS Pass Rate</p>
                        </div>
                        <div className="text-center p-8 bg-white rounded-xl border border-slate-200">
                            <p className="text-4xl font-bold text-[#2C25C4] mb-2">10k+</p>
                            <p className="text-gray-600">Jobs Landed</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-8 lg:px-12">
                <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#2C25C4] to-[#8C18C3] rounded-2xl p-8 sm:p-12 text-center space-y-6">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white">
                        Ready to Land Your Dream Job?
                    </h2>
                    <p className="text-lg text-blue-100 max-w-2xl mx-auto">
                        Don't let a poorly formatted resume hold you back. Book a slot with our experts today and get a resume that gets results.
                    </p>
                    <button
                        onClick={handleBookSlot}
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#2C25C4] rounded-lg font-semibold hover:bg-blue-50 transition-all duration-300 text-base sm:text-lg hover:shadow-lg"
                    >
                        <HiOutlineCalendarDays className="w-5 h-5" />
                        Book Your Slot
                    </button>
                </div>
            </section>
        </div>
    )
}

export default PrepareResume